import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './ToolHeader.css';

/**
 * ToolHeader — shared sticky step navigator for every tool screen.
 *
 * Shows a compact stepper (coloured pips + the current step name).
 * Tap it to drop the full labelled list and jump to any step — backward
 * freely, forward when you want to skip ahead. Replaces the old
 * back-chevron; the browser Back button still works as before.
 *
 * Props:
 *   num         — e.g. "03" (display fallback; current step is resolved
 *                 from the route)
 *   title       — e.g. "Draft"
 *   savedFlash  — show the "Saved" pill briefly when state changes
 */

// Four steps. The four-step refactor folded "Pages" into Logistics and
// merged "Brainstorm" + "Design" into a single "Draft" stop on the
// /tools/draft path (old /tools/brainstorm and /tools/design redirect to
// it). Draft keeps Brainstorm's plum. The pip count and "of NN" label
// derive from this array automatically, so this is the only edit needed.
const STEPS = [
  { num: '01', label: 'Logistics', path: '/tools/logistics', color: '#3a8a63' }, // forest
  { num: '02', label: 'Objective', path: '/tools/objective', color: '#e8b547' }, // gold
  { num: '03', label: 'Draft',     path: '/tools/draft',     color: '#a05ec1' }, // plum
  { num: '04', label: 'Visuals',   path: '/tools/builder',   color: '#2f8f5b' }, // green (internal path stays /tools/builder)
];

export default function ToolHeader({ num, title, savedFlash = false }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Resolve the current step from the route, falling back to the num prop.
  let currentIdx = STEPS.findIndex((s) => pathname.startsWith(s.path));
  if (currentIdx < 0) {
    currentIdx = STEPS.findIndex((s) => parseInt(s.num, 10) === parseInt(num, 10));
  }
  const current = STEPS[currentIdx] ?? { num: num ?? '', label: title };

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const stateFor = (i) => (i < currentIdx ? 'done' : i === currentIdx ? 'current' : 'ahead');

  const goTo = (step) => {
    setOpen(false);
    if (step.path) navigate(step.path);
  };

  return (
    <header className="tool-header" ref={wrapRef}>
      <button
        type="button"
        className="tool-stepper"
        aria-expanded={open}
        aria-label={`Step ${current.num} of ${STEPS.length}: ${current.label}. Tap to jump to another step.`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="tool-stepper__pips" aria-hidden="true">
          {STEPS.map((s, i) => {
            const state = stateFor(i);
            const style =
              state === 'current' ? { background: s.color, borderColor: s.color }
              : state === 'done'  ? { borderColor: s.color, color: s.color }
              : undefined;
            return (
              <span key={s.num} className={`pip pip--${state}`} style={style}>
                {state === 'done' && (
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" aria-hidden="true">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </span>
            );
          })}
        </span>

        <span className="tool-stepper__text">
          <span className="eyebrow">{current.num} of {String(STEPS.length).padStart(2, '0')}</span>
          <span className="tool-stepper__title">{title}</span>
        </span>

        <svg
          className={`tool-stepper__chev ${open ? 'is-open' : ''}`}
          width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      <span className={`tool-header__saved ${savedFlash ? 'is-visible' : ''}`} aria-live="polite">
        Saved
      </span>

      {open && (
        <ul className="tool-menu" role="menu">
          {STEPS.map((s, i) => {
            const state = stateFor(i);
            return (
              <li key={s.num} role="none">
                <button
                  type="button"
                  role="menuitem"
                  className={`tool-menu__item is-${state}`}
                  aria-current={state === 'current' ? 'step' : undefined}
                  onClick={() => goTo(s)}
                >
                  <span className="tool-menu__num" style={{ background: s.color }}>
                    {state === 'done' ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" aria-hidden="true">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    ) : s.num}
                  </span>
                  <span className="tool-menu__label">{s.label}</span>
                  {state === 'current' && <span className="tool-menu__here">You're here</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </header>
  );
}
